import { siteApiSlice } from "../../app/siteApiSlice";
import { UserRole } from "../../app/userSlice";
import NamedEntity from "../../interfaces/NamedEntity";

export interface CourseSelectViewModel {
	userId: number;
	role: UserRole;
	showAddCourseLink: boolean;
	onlyShowActive: boolean;
	showEnrollmentSection: boolean;
	showSchoolColumn: boolean;
	alerts: Record<number, string>;
	clearDismissedAlerts: boolean;
}

export interface CourseRegistrationsViewModel {
	courses: CourseRegistrationViewModel[];
}

export interface CourseRegistrationViewModel {
	courseId: number;
	createDate: string;
	status: string;
	courseReferralCode: string;
	courseGrade: string;
	closeDate: string;
	courseName: string;
	isFavorite: boolean;
	school: string;
}

export interface ChangeFavoriteModel {
	userCourseId: number;
	favoriteState: boolean;
}

export const EmptyCourseRegistrationsViewModel: CourseRegistrationsViewModel = {
	courses: [],
};

export interface CreateCourseViewModel {
	districtCode: string;
	currentSchools: NamedEntity[];
	isDateRangeRequired: boolean;
	showScoringLevelExplanation: boolean;
}

export interface CreateCourseEditModel {
	name: string;
	school: NamedEntity;
	gradeId: number;
	beginDate?: Date;
	endDate?: Date;
}

export interface AdminEditCourseViewModel {
	id: number;
	schoolId: number;
	districtName: string;
	schoolName: string;
	schoolExternalId: string;
	courseReferralCode: string;
	proctors: CourseUserViewModel[];
	students: CourseUserViewModel[];
	cleverID: string;
	showCleverId: boolean;
	classLinkID: string;
	showClassLinkId: boolean;
	editModel: AdminEditCourseEditModel;
}

export interface AdminEditCourseEditModel {
	name: string;
	gradeId: number;
	active: boolean;
	externalID: string;
	disableUserImages: boolean;
	disableUserImagesByDistrict: boolean;
	disableUserImagesBySchool: boolean;
}

export interface CourseUserViewModel {
	userId: number;
	name: string;
	username: string;
	initials: string;
	hasImage: boolean;
	externalId: string;
	classLinkId: string;
	cleverId: string;
	email: string;
	lastLoggedIn: Date;
}

export interface CourseOutsideSiteViewModel {
	courseId: number;
	lastSyncAt: Date;
}

export interface CourseCleverSettingsViewModel extends CourseOutsideSiteViewModel {
	cleverId: string;
}

export interface CourseClassLinkSettingsViewModel extends CourseOutsideSiteViewModel {
	classLinkId: string;
}

export const coursesApiSlice = siteApiSlice
	.enhanceEndpoints({
		addTagTypes: ["CourseCleverSettings", "CourseClassLinkSettings"],
	})
	.injectEndpoints({
		endpoints: (build) => ({
			getSelectCoursePage: build.query<CourseSelectViewModel, void>({
				query: () => `/SignIn/SelectCourse`,
				providesTags: ["Alerts"],
			}),
			getCourseEnrollments: build.query<CourseRegistrationsViewModel, boolean>({
				query: (getAll) => ({
					url: `/SignIn/GetCourseEnrollments`,
					params: { getAll },
				}),
				providesTags: ["CourseRegistrations"],
			}),
			changeFavorite: build.mutation<void, ChangeFavoriteModel>({
				query: (payload) => ({
					url: `/SignIn/UpdateFavoriteCourse`,
					method: "POST",
					body: payload,
				}),
			}),
			makeCourseSelection: build.mutation<boolean, number>({
				query: (courseId) => ({
					url: `/SignIn/MakeCourseSelection/${courseId}`,
					method: "GET",
				}),
				invalidatesTags: ["CurrentCourse"],
			}),
			registerForCourse: build.mutation<void, string>({
				query: (courseCode) => ({
					url: `/Registration/RegisterForCourse`,
					params: { courseCode },
					method: "GET",
				}),
				invalidatesTags: ["CourseRegistrations"],
			}),
			getCreateCourse: build.query<CreateCourseViewModel, string | undefined>({
				query: (districtCode?) => ({
					url: `/Registration/CreateCourse`,
					params: districtCode ? { districtCode } : undefined,
					method: "GET",
				}),
				providesTags: ["CourseRegistrations"],
			}),
			createCourse: build.mutation<string, CreateCourseEditModel>({
				query: (payload) => ({
					url: `/Registration/CreateCourseForSpa`,
					body: payload,
					method: "POST",
				}),
				invalidatesTags: ["CourseRegistrations"],
			}),
			getEditCourseInfo: build.query<AdminEditCourseViewModel, number>({
				query: (id) => ({
					url: `/Course/Edit`,
					params: { id },
				}),
			}),
			editCourse: build.mutation<void, { id: number; editModel: AdminEditCourseEditModel }>({
				query: (q) => {
					const { id, editModel } = q;
					return {
						url: `/Course/Edit`,
						method: "POST",
						body: { id, editModel },
					};
				},
				invalidatesTags: ["School", "Schools"],
			}),
			updateActiveSettingForCourse: build.mutation<
				void,
				{ courseId: number; active: boolean }
			>({
				query: (payload) => {
					return {
						url: `/Course/UpdateActiveSettingForCourse`,
						method: "POST",
						body: payload,
					};
				},
				invalidatesTags: ["School", "Schools"],
			}),
			updateUserAvatarSettingForCourse: build.mutation<
				void,
				{ courseId: number; disabled: boolean }
			>({
				query: (payload) => {
					return {
						url: `/Course/UpdateAvatarSettingForCourse`,
						method: "POST",
						body: payload,
					};
				},
				invalidatesTags: ["School", "Schools"],
			}),
			getCourseCleverSettings: build.query<CourseCleverSettingsViewModel, number>({
				query: (courseId) => ({
					url: `/Course/EditCleverSettings`,
					method: "GET",
					params: { courseId },
				}),
				providesTags: ["CourseCleverSettings"],
			}),
			updateCourseCleverSettings: build.mutation<void, CourseCleverSettingsViewModel>({
				query: (payload) => ({
					url: `/Course/EditCleverSettings`,
					method: "POST",
					body: payload,
				}),
				invalidatesTags: ["School", "CourseCleverSettings"],
			}),
			getCourseClassLinkSettings: build.query<CourseClassLinkSettingsViewModel, number>({
				query: (courseId) => ({
					url: `/Course/EditClassLinkSettings`,
					method: "GET",
					params: { courseId },
				}),
				providesTags: ["CourseClassLinkSettings"],
			}),
			updateCourseClassLinkSettings: build.mutation<void, CourseClassLinkSettingsViewModel>({
				query: (payload) => ({
					url: `/Course/EditClassLinkSettings`,
					method: "POST",
					body: payload,
				}),
				invalidatesTags: ["School", "CourseClassLinkSettings"],
			}),
		}),
	});

export const {
	useGetSelectCoursePageQuery,
	useGetCourseEnrollmentsQuery,
	useChangeFavoriteMutation,
	useMakeCourseSelectionMutation,
	useRegisterForCourseMutation,
	useGetCreateCourseQuery,
	useCreateCourseMutation,
	useLazyGetEditCourseInfoQuery,
	useEditCourseMutation,
	useUpdateActiveSettingForCourseMutation,
	useUpdateUserAvatarSettingForCourseMutation,
	useGetCourseCleverSettingsQuery,
	useUpdateCourseCleverSettingsMutation,
	useGetCourseClassLinkSettingsQuery,
	useUpdateCourseClassLinkSettingsMutation,
} = coursesApiSlice;
